import React, { useRef } from 'react';
/**
 * DjayAvatar — render primitive (orchestrator).
 *
 * Split from the former ~1619-line `DjayAvatar.tsx` god-file into a scalable
 * `mascot/avatar/` module (MASCOT_SSOT_ARCHITECTURE_AUDIT_2026 §4.1). This
 * orchestrator owns ONLY: size→detail-flag derivation, the unique gradient
 * `uid`, the outer <svg> shell + body gradient <defs>, and the assembly of the
 * body layers + the always-rendered floating accessory. All per-feature SVG
 * lives in `./parts/*`.
 *
 * This stays the canonical render primitive: product code must use the intent
 * wrappers `<DjayIdentity>` / `<DjaySpecimen>` / `<DjayNarrator>` instead of
 * rendering `<DjayAvatar>` directly (lint-guarded, see .eslintrc.json §6/§9.6).
 *
 * Import note (audit ronde 10): only `useRef` is needed from React — JSX is
 * transformed by the bundler. Adding unused React imports has regressed twice.
 */

import { SIZES, DJAY_VIEWBOX, DETAIL_THRESHOLD, ARM_STUB_MIN, MOUTH_MIN } from './types';
import type { DjayAvatarProps } from './types';
import { VARIANT_COLORS } from './palette';
import { ensureAnimationStyles } from './animationStyles';
import { BodyLayers } from './parts/BodyLayers';
import { Accessory } from './parts/Accessory';
export function DjayAvatar({
  size = 'md',
  emotion = 'happy',
  tier = 'rookie',
  variant = 'default',
  accessory = 'none',
  className = '',
  animate = true,
  hideBody = false
}: DjayAvatarProps) {
  const pixelSize = SIZES[size];
  const showDetails = pixelSize >= DETAIL_THRESHOLD;
  const showArmStubs = pixelSize >= ARM_STUB_MIN && pixelSize < DETAIL_THRESHOLD;
  const showFullArms = pixelSize >= DETAIL_THRESHOLD;
  const showTail = pixelSize >= DETAIL_THRESHOLD;
  const showMouth = pixelSize >= MOUTH_MIN;
  // If hideBody is true, force showAccessory regardless of size so we can
  // render just the accessory.
  const showAccessory = (pixelSize >= DETAIL_THRESHOLD || hideBody) && accessory !== 'none';
  const uidRef = useRef(`djay-${size}-${variant}-${Math.random().toString(36).slice(2, 6)}`);
  const uid = uidRef.current;
  const colors = VARIANT_COLORS[variant] ?? VARIANT_COLORS.default;
  // Inject animation styles once (singleton)
  if (animate) ensureAnimationStyles();
  return <div className={`relative inline-flex items-center justify-center ${className}`} data-id="element-2962">
      <svg width={pixelSize} height={pixelSize} viewBox={DJAY_VIEWBOX} fill="none" xmlns="http://www.w3.org/2000/svg" className={`djay-avatar ${animate ? 'djay-animated' : ''}`} style={{
      overflow: 'visible'
    }} role="img" aria-label={`Djay mascotte (${emotion})`} data-id="element-2963">
        <defs data-id="element-2964">
          {variant === 'rainbow' ? <linearGradient id={`${uid}-body-grad`} x1="35" y1="45" x2="165" y2="195" gradientUnits="userSpaceOnUse" data-id="element-2965">
              <stop offset="0%" stopColor="#F87171" data-id="element-2966" />
              <stop offset="20%" stopColor="#FB923C" data-id="element-2967" />
              <stop offset="40%" stopColor="#FBBF24" data-id="element-2968" />
              <stop offset="60%" stopColor="#34D399" data-id="element-2969" />
              <stop offset="80%" stopColor="#60A5FA" data-id="element-2970" />
              <stop offset="100%" stopColor="#A78BFA" data-id="element-2971" />
            </linearGradient> : <linearGradient id={`${uid}-body-grad`} x1="35" y1="45" x2="165" y2="195" gradientUnits="userSpaceOnUse" data-id="element-2972">
              <stop offset="0%" stopColor={colors.bodyGradient[0]} data-id="element-2973" />
              <stop offset="45%" stopColor={colors.bodyGradient[1]} data-id="element-2974" />
              <stop offset="100%" stopColor={colors.bodyGradient[2]} data-id="element-2975" />
            </linearGradient>}
        </defs>
        {!hideBody && <BodyLayers uid={uid} colors={colors} emotion={emotion} tier={tier} variant={variant} showTail={showTail} showDetails={showDetails} showMouth={showMouth} showFullArms={showFullArms} showArmStubs={showArmStubs} data-id="element-2976" />}
        <g className="djay-accessory-float" data-id="element-2977">
          <Accessory accessory={accessory} uid={uid} showAccessory={showAccessory} data-id="element-2978" />
        </g>
      </svg>
    </div>;
}